import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { BookOpen, Lock, Sparkles, Play, ArrowLeft, Loader2, Check, Share2, CheckCircle2 } from "lucide-react";

const FILES = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;
const LOGIN_URL = (next) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/audio/auth/google/login?next=${encodeURIComponent(next)}`;

export default function EComicsVolume() {
  const { volumeId } = useParams();
  const [vol, setVol] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/ecomics/volumes/${volumeId}`);
      setVol(data);
    } catch { setNotFound(true); }
  }, [volumeId]);
  useEffect(() => { load(); }, [load]);

  const buy = async () => {
    if (!vol.logged_in) {
      window.location.href = LOGIN_URL(`/ecomics/volume/${volumeId}`);
      return;
    }
    setBusy(true);
    try {
      const { data: order } = await api.post("/ecomics/purchase/create-order", { volume_id: volumeId });
      if (typeof window.Razorpay === "undefined") {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = res; s.onerror = rej;
          document.body.appendChild(s);
        });
      }
      const rz = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Bala Basava E-Comics",
        description: order.volume_title,
        order_id: order.order_id,
        theme: { color: "#C25946" },
        handler: async (response) => {
          try {
            await api.post("/ecomics/purchase/verify", {
              volume_id: volumeId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Volume unlocked!");
            load();
          } catch (e) { toast.error(formatError(e)); }
        },
      });
      rz.open();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const share = async () => {
    const url = window.location.href;
    const text = `Bala Basava E-Comics — Volume ${vol.volume_number}: ${vol.title}. Read, listen, explore and colour! ${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: `Bala Basava E-Comics · ${vol.title}`, text, url }); return; } catch { /* cancelled */ }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  if (notFound) {
    return (
      <PublicLayout>
        <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="ecomics-volume-404">
          <div className="font-serif text-3xl text-[var(--bb-forest)]">Volume not found</div>
          <Link to="/ecomics" className="bb-link mt-4 inline-block">← Back to E-Comics</Link>
        </div>
      </PublicLayout>
    );
  }
  if (!vol) return <PublicLayout><div className="p-24 text-center text-[var(--bb-muted)]">Loading…</div></PublicLayout>;

  const symbol = vol.currency === "INR" ? "₹" : vol.currency;

  return (
    <PublicLayout>
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="ecomics-volume-page">
        <Link to="/ecomics" className="bb-link inline-flex items-center gap-1 text-sm mb-6" data-testid="ecomics-volume-back"><ArrowLeft size={14}/> All volumes</Link>

        <div className="grid md:grid-cols-12 gap-8 md:gap-10">
          <div className="md:col-span-4">
            <div className="aspect-[3/4] rounded overflow-hidden bg-[var(--bb-sand)] ring-1 ring-[var(--bb-border)]">
              {vol.cover_path ? (
                <img src={FILES + vol.cover_path} alt={vol.title} className="w-full h-full object-cover"/>
              ) : (
                <div className="w-full h-full grid place-items-center text-[var(--bb-muted)]"><BookOpen size={40}/></div>
              )}
            </div>
          </div>
          <div className="md:col-span-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="bb-overline text-[var(--bb-terracotta)] mb-2">VOLUME {vol.volume_number}</div>
                <h1 className="font-serif text-4xl sm:text-5xl text-[var(--bb-forest)] tracking-tight" data-testid="ecomics-volume-title">{vol.title}</h1>
              </div>
              <button onClick={share} className="shrink-0 mt-1 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[var(--bb-border)] bg-white text-sm text-[var(--bb-forest)] hover:border-[var(--bb-terracotta)] hover:text-[var(--bb-terracotta)] transition" data-testid="ecomics-share-btn">
                <Share2 size={14}/> Share
              </button>
            </div>
            <p className="mt-4 text-[var(--bb-ink)]/80 leading-relaxed">{vol.description}</p>

            <div className="mt-7 bb-card p-5 bg-white" data-testid="ecomics-purchase-card">
              {vol.purchased ? (
                <div className="flex items-center gap-2 text-[var(--bb-forest)] font-medium" data-testid="ecomics-owned-badge">
                  <Check size={16} className="text-green-600"/> You own this volume — read everything below.
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="font-serif text-3xl text-[var(--bb-forest)]" data-testid="ecomics-volume-price">{symbol}{vol.price}</div>
                      <div className="text-xs text-[var(--bb-muted)]">One-time · unlocks all vachanas in this volume</div>
                    </div>
                    {vol.sales_active ? (
                      <button onClick={buy} disabled={busy} className="bb-btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="ecomics-buy-btn">
                        {busy ? <Loader2 size={15} className="animate-spin"/> : <Sparkles size={15}/>}
                        {vol.logged_in ? `Buy for ${symbol}${vol.price}` : "Sign in with Google to buy"}
                      </button>
                    ) : (
                      <span className="bb-tag" data-testid="ecomics-sales-off">Purchases temporarily unavailable</span>
                    )}
                  </div>
                  <p className="mt-3 text-xs text-[var(--bb-muted)]">First vachana is free to read. Payments via Razorpay.</p>
                </>
              )}
            </div>

            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <div className="bb-overline text-[var(--bb-muted)]">VACHANAS ({vol.vachanas.length})</div>
                {vol.logged_in && vol.vachanas.length > 0 && (() => {
                  const read = vol.vachanas.filter(v => vol.progress?.[v.vachana_id]?.finished).length;
                  return (
                    <div className="flex items-center gap-2 text-xs text-[var(--bb-muted)]" data-testid="ecomics-volume-read-count">
                      <div className="w-24 h-1.5 rounded-full bg-white ring-1 ring-[var(--bb-border)] overflow-hidden">
                        <div className="h-full bg-[var(--bb-terracotta)] rounded-full" style={{ width: `${Math.round((read / vol.vachanas.length) * 100)}%` }}/>
                      </div>
                      {read} of {vol.vachanas.length} read
                    </div>
                  );
                })()}
              </div>
              {vol.vachanas.length === 0 ? (
                <div className="bb-card p-8 text-center text-sm text-[var(--bb-muted)]" data-testid="ecomics-no-vachanas">Vachanas coming soon.</div>
              ) : (
                <div className="space-y-2.5">
                  {vol.vachanas.map((v) => {
                    const unlocked = vol.purchased || v.vachana_id === vol.free_vachana_id;
                    return (
                      <Link
                        key={v.vachana_id}
                        to={unlocked ? `/ecomics/read/${vol.volume_id}/${v.vachana_id}` : "#"}
                        onClick={(e) => { if (!unlocked) { e.preventDefault(); toast.info(`Buy this volume for ${symbol}${vol.price} to read all vachanas.`); } }}
                        className={`bb-card p-4 flex items-center gap-4 bg-white transition ${unlocked ? "hover:shadow-[0_14px_30px_-18px_rgba(44,53,49,0.3)]" : "opacity-75"}`}
                        data-testid={`ecomics-vachana-item-${v.vachana_id}`}
                      >
                        <div className="w-9 h-9 rounded-full bg-[var(--bb-terracotta)]/10 text-[var(--bb-terracotta)] grid place-items-center font-serif shrink-0">{v.sequence_number}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[var(--bb-forest)] truncate flex items-center gap-1.5">
                            {v.kannada_title}
                            {vol.progress?.[v.vachana_id]?.finished && (
                              <CheckCircle2 size={15} className="text-green-600 shrink-0" title="Finished" data-testid={`ecomics-read-tick-${v.vachana_id}`}/>
                            )}
                          </div>
                          <div className="text-xs text-[var(--bb-muted)] truncate">{v.english_title}</div>
                        </div>
                        {v.vachana_id === vol.free_vachana_id && !vol.purchased && (
                          <span className="bb-tag bb-tag-green shrink-0" data-testid={`ecomics-free-tag-${v.vachana_id}`}>FREE PREVIEW</span>
                        )}
                        {unlocked
                          ? <Play size={16} className="text-[var(--bb-terracotta)] shrink-0" data-testid={`ecomics-read-icon-${v.vachana_id}`}/>
                          : <Lock size={15} className="text-[var(--bb-muted)] shrink-0" data-testid={`ecomics-lock-icon-${v.vachana_id}`}/>}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
